<template>
  <div id="app" :class="[{ rtl: isRtl, webview: isWebview }, currentLanguage]">
    <v-spinner :key="route"></v-spinner>
    <v-notification :key="route"></v-notification>
    <v-modal :key="route"></v-modal>
    <router-view></router-view>
  </div>

  <DevMenuClass v-if="showDevMenu" />
</template>

<script>
import '@/assets/stylus/app.styl'

import { SpanStatusCode } from '@opentelemetry/api'
import { defineAsyncComponent } from 'vue'
import { useWindowSize } from 'vue-window-size'
import { mapActions, mapGetters } from 'vuex'

import VModal from '@/components/Modal/Modal.vue'
import VNotification from '@/components/Notification.vue'
import VSpinner from '@/components/Spinner.vue'
import { useOpenTelemetry } from '@/composables/useOpenTelemetry'
import { useWebview } from '@/composables/useWebview'

import { useAppEnvironment } from './composables/useAppEnvironment'
import { useSyncModule } from './composables/useSyncModule'
import { setTierSupport } from './utils/session'

export default {
  /**
   * The name of the application.
   */
  name: 'EwaTng',

  /**
   * Component that could be used in template
   */
  components: {
    VNotification,
    VSpinner,
    VModal,
    DevMenuClass: defineAsyncComponent(
      () => import('@/components/DevMenu/DevMenuClass.vue')
    )
  },

  setup() {
    const { width } = useWindowSize()
    const { isWebview } = useWebview()
    const { tracer, initOpenTelemetry } = useOpenTelemetry()
    const { showDevMenu } = useAppEnvironment()
    const { syncStore, startSyncModule, isSyncProtocolActive } = useSyncModule()

    return {
      windowWidth: width,
      isWebview,
      initOpenTelemetry,
      tracer,
      showDevMenu,
      syncStore,
      startSyncModule,
      isSyncProtocolActive
    }
  },

  data() {
    return {
      modalIsOpened: false
    }
  },

  computed: {
    ...mapGetters('account', {
      isPremium: 'isPremium',
      getUserId: 'getUserId',
      getBill: 'getBill',
      currentLanguage: 'getCurrentLanguage',
      isBillPending: 'isBillPending',
      getPlanType: 'getPlanType',
      getHasInstallSigns: 'getHasInstallSigns',
      userId: 'getUserId',
      getActiveProfile: 'getActiveProfile',
      getCurrentLanguage: 'getCurrentLanguage'
    }),
    route() {
      return this.$route
    },
    auth() {
      return this.$route.meta.auth
    },
    isRtl() {
      const rtlLangs = ['fa', 'he', 'ar']
      return rtlLangs.includes(this.currentLanguage)
    }
  },

  watch: {
    getUserId(id) {
      if (!id) {
        return
      }

      this.$setCustomerUserId(id)
      this.initOpenTelemetry()

      if (this.isSyncProtocolActive) {
        this.startSyncModule()
      }
    }
  },

  async mounted() {
    await this.detectLocation()
    setTierSupport()

    this.initOpenTelemetry()

    window.addEventListener('error', this.handleGlobalError)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
  },

  unmounted() {
    window.removeEventListener('error', this.handleGlobalError)
    window.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection
    )
  },

  methods: {
    ...mapActions('modal', {
      open: 'open',
      setOptions: 'setOptions'
    }),
    ...mapActions('auth', ['check', 'logout']),
    ...mapActions('account', ['detectLocation']),
    handleGlobalError(event) {
      const span = this.tracer?.startSpan('global-error')

      if (span) {
        span.recordException(event.error)
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: event.error.message
        })

        span.end()
      }
    },
    handleUnhandledRejection(event) {
      const span = this.tracer?.startSpan('unhandled-promise-rejection')

      if (span) {
        span.recordException(event.reason)
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message:
            event.reason && event.reason.message
              ? event.reason.message
              : 'Unhandled promise rejection'
        })
        span.end()
      }
    }
  }
}
</script>
